<template>
  <q-card
    :class="{
      'bg-transparent': bgTransparent,
      'bg-responsive-white': !bgTransparent,
      'q-pa-lg': !dense
    }"
    style="width: 100%; max-width: 500px;"
    data-test-id="account-login-card"
  >
    <q-form
      ref="formRef"
      @submit.prevent="login"
    >
      <div class="column">
        <slot name="header">
          <div class="text-h6 q-mb-lg">
            {{ $kiosk ? $t('user.actions.find') : $t('auth.actions.login') }}
          </div>
        </slot>
        <login-help-banner v-if="$consumer" />
        <q-input
          id="email"
          v-model.trim="user.email"
          class="col"
          :class="{ 'q-pb-sm': $q.screen.lt.sm }"
          type="email"
          color="dark"
          bg-color="white"
          :dark="false"
          :label="$t('form.labels.emailAddress')"
          :rules="[
            val => required.$validator(val) || $t('form.errors.required'),
            val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
          ]"
          lazy-rules
          :autofocus="!user.email && autofocusEnabled"
          bottom-slots
          :hide-bottom-space="$q.screen.lt.sm"
          data-test-id="email-input"
        />
        <q-input
          id="password"
          v-model="user.password"
          class="col"
          :class="{ 'q-pb-sm': $q.screen.lt.sm }"
          type="password"
          :label="$t('form.labels.password')"
          :rules="[val => required.$validator(val) || $t('form.errors.required')]"
          color="dark"
          bg-color="white"
          :dark="false"
          lazy-rules
          bottom-slots
          :hide-bottom-space="$q.screen.lt.sm"
          :autofocus="!!user.email"
          data-test-id="password-input"
          @keyup.enter="login"
        />
        <q-btn
          class="full-width q-pb-md"
          size="lg"
          color="primary"
          :text-color="buttonTextColor"
          :loading="loading"
          :label="$kiosk ? $t('user.actions.find') : $t('auth.actions.login')"
          data-test-id="login-submit-button"
          type="submit"
        />
        <div
          class="row items-center"
          :class="{
            'justify-center': hideRegisterButton,
            'justify-between': !hideRegisterButton,
            'q-mt-lg': !$q.screen.lt.sm,
            'q-mt-xl': $q.screen.gt.xs
          }"
        >
          <q-btn
            v-if="!hideRegisterButton"
            flat
            color="primary"
            :label="$t('auth.actions.signup')"
            data-test-id="register-link"
            @click="$kiosk && $emit('register') || $router.push('/register')"
          />
          <q-btn
            type="button"
            flat
            color="secondary"
            :label="$t('auth.actions.goToForgotPassword')"
            to="/password/forgot"
            data-test-id="forgot-password-link"
          />
        </div>
        <slot name="default" />
      </div>
    </q-form>
  </q-card>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { Dialog } from 'quasar'
import Model from 'src/models/model'
import User from 'src/models/user/User'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'
import LoginHelpBanner from 'src/modules/checkout/components/LoginHelpBanner'
export default {
  name: 'Login',
  components: {
    LoginHelpBanner
  },
  mixins: [FormValidationMixin],
  configuration: ['currentSite'],
  props: {
    kioskLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    consumerLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    postAuthPath: {
      type: String,
      default: '/',
      required: false
    },
    hideRegisterButton: {
      type: Boolean,
      default: false,
      required: false
    },
    bgTransparent: {
      type: Boolean,
      default: false,
      required: false
    },
    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    autofocusEnabled: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  emits: ['register', 'login-success'],
  data () {
    return {
      user: null,
      loading: false
    }
  },
  computed: {
    buttonTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    }
  },
  created () {
    _.extend(this, { email, required })
  },
  beforeMount () {
    this.user = new User()
  },
  mounted () {
    const authData = this.$auth.getAuthData()
    if (authData.email) {
      this.user.email = authData.email
    }
  },
  unmounted () {
    this.user.destroy()
  },
  methods: {
    login () {
      this.validateForm(async () => {
        this.loading = true

        const { data, errors } = await this.user.logIn(this.kioskLogin)

        if ((errors && errors.length) || (data.errors && data.errors.length)) {
          if (errors[0].code === 'no_password_set') {
            Dialog.create({
              title: 'Account Exists',
              message: 'Your account exists but no password has been set. We will send you an email you can use to set your password.'
            }).onDismiss(() => {
              if (!this.kioskLogin) {
                this.$router.push('/password/claim-account')
              }
            })

            this.loading = false
            return
          } else {
            this.$mh.showErrors(errors || data.errors)
            this.loading = false
            return
          }
        }
        if (this.kioskLogin || this.consumerLogin) {
          this.$emit('login-success', data?.user?.id)
          this.loading = false
          return
        }
        if (data.user) {
          await Model.configuration.validate()
          await Model.context.fetch(true)
          this.$auth.updateCableSubscriptions()
          this.$auth.navigatePostAuth(this.postAuthPath)
        }
        this.loading = false
      })
    }
  }
}
</script>
